import React, { useEffect, useState } from "react";
import { GetAllReceivedCandidate } from "../Services/NewApplicationService";
import {
  ShortlistCandidate,
  RejectedCandidates,
  GetCandidateDetails,
  viewResumeUrl,
  SystemRemark,
} from "../../CandidateServices/CandidateService";
import NormalAlert from "../../../../Alerts/NormalAlert";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Funnel, MoreVertical, FileText } from "lucide-react";
import ColumnFilterModal from "../../../CommonModals/ColumnFilterModal";
import Pagination from "../../../CommonModals/Pagination";
import ViewResumeModal from "../../../Jobs/SendCandidates/Modals/ViewResumeModal";
import CandidateDetailsModal from "../Modal/CandidateDetailsModal";
import SystemRemarkModal from "../Modal/SystemRemarkModal";

const NewApplications = () => {
  //  DATA
  const [candidates, setCandidates] = useState([]);

  //  UI
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUrl, setResumeUrl] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [systemRemarkData, setSystemRemarkData] = useState(null);
  const [showSystemRemark, setShowSystemRemark] = useState(false);
  const [showSystemRemarkModal, setShowSystemRemarkModal] = useState(false);
  const [systemRemarkLoading, setSystemRemarkLoading] = useState(false);
  const [modalActionType, setModalActionType] = useState(null);
  const [remark, setRemark] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  //  SEARCH
  const [searchTerm, setSearchTerm] = useState("");

  //  FILTER
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const [filters, setFilters] = useState({
    jobTitel: [],
    submitByType: [],
    functionName: [],
    department: [],
    grade: [],
  });

  //  PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const [candidatesPerPage, setCandidatesPerPage] = useState(10);

  //  FETCH

  const fetchCandidates = async () => {
    try {
      const response = await GetAllReceivedCandidate();
      const data = response?.data;

      if (!data) throw new Error();

      setCandidates(data.receivedCandidates || []);

      if (data.status === "Fail" && data.message !== "No candidate found.") {
        setAlert({
          type: "fail",
          message: data.message || "Something went wrong",
        });
      }
    } catch (error) {
      const backendMessage = error?.response?.data?.message;

      if (backendMessage !== "No candidate found.") {
        setAlert({
          type: "fail",
          message: backendMessage || "Something went wrong. Please try again.",
        });
      }

      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCandidates();
      setLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".action-menu")) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleResumeClick = async (candidate) => {
    if (!candidate.resumePath) {
      setAlert({
        type: "fail",
        message: "Resume not available.",
      });
      return;
    }

    try {
      const url = await viewResumeUrl(candidate.resumePath);
      setResumeUrl(url);
      setShowResumeModal(true);
    } catch (error) {
      setAlert({
        type: "fail",
        message: "Failed to load resume.",
      });
    }
  };

  const handleNameClick = async (candidate) => {
    try {
      setShowModal(true);
      const res = await GetCandidateDetails(candidate.submitedCandidateUID);

      if (res?.data?.status === "Success") {
        setCandidateDetails(res.data);
        setSelectedCandidate(candidate);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // handle button for System Remark

  const handleViewSystemRemark = async () => {
    try {
      setSystemRemarkLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const response = await SystemRemark(
        selectedCandidate.submitedCandidateUID,
      );

      if (response?.data?.status === "Success") {
        setSystemRemarkData(response.data.data);
        setShowSystemRemarkModal(true);
      } else {
        setAlert({
          type: "fail",
          message: "Failed to fetch system remark",
        });
      }
    } catch (error) {
      setAlert({
        type: "fail",
        message: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setSystemRemarkLoading(false);
    }
  };

  const filterOptions = {
    jobTitel: [...new Set(candidates.map((c) => c.jobTitel))],
    submitByType: [...new Set(candidates.map((c) => c.submitByType))],
    functionName: [...new Set(candidates.map((c) => c.functionName))],
    department: [...new Set(candidates.map((c) => c.department))],
    grade: [...new Set(candidates.map((c) => c.grade))],
  };

  const filteredCandidates = candidates.filter((candidate) => {
    const matchesJobTitle =
      (filters.jobTitel || []).length === 0 ||
      (filters.jobTitel || []).includes(candidate.jobTitel);

    const matchesSubmitType =
      (filters.submitByType || []).length === 0 ||
      (filters.submitByType || []).includes(candidate.submitByType);

    const matchesFunction =
      (filters.functionName || []).length === 0 ||
      (filters.functionName || []).includes(candidate.functionName);

    const matchesDepartment =
      (filters.department || []).length === 0 ||
      (filters.department || []).includes(candidate.department);

    const matchesGrade =
      (filters.grade || []).length === 0 ||
      (filters.grade || []).includes(candidate.grade);

    const matchesSearch = [
      candidate.jobTitel,
      candidate.submitByType,
      candidate.functionName,
      candidate.department,
      candidate.grade,
      candidate.name,
      candidate.email,
    ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
      matchesJobTitle &&
      matchesSubmitType &&
      matchesFunction &&
      matchesDepartment &&
      matchesGrade &&
      matchesSearch
    );
  });

  //  PAGINATION
  const indexOfLast = currentPage * candidatesPerPage;
  const indexOfFirst = indexOfLast - candidatesPerPage;
  const currentCandidates = filteredCandidates.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  //  ACTIONS
  const handleFinalAction = async () => {
    try {
      setActionLoading(true);

      let res;

      if (modalActionType === "shortlist") {
        res = await ShortlistCandidate(
          selectedCandidate.submitedCandidateUID,
          2,
          remark,
        );
      }

      if (modalActionType === "reject") {
        res = await RejectedCandidates(
          selectedCandidate.submitedCandidateUID,
          2,
          remark,
        );
      }

      setAlert({
        type: res.data.status,
        message: res.data.message,
      });

      setShowModal(false);
      setModalActionType(null);
      setRemark("");

      await fetchCandidates();
    } catch (error) {
      setAlert({
        type: "fail",
        message: error?.response?.data?.message,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    try {
      return dateStr.split("T")[0];
    } catch {
      return "";
    }
  };

  //  UI
  return (
    <>
      <div className="w-full py-8 mt-12">
        <div className="max-w-[1240px] mx-auto">
          <div className="bg-white rounded-2xl px-6 py-6 shadow-sm">
            {/* HEADER */}
            <div className="bg-master-header text-white rounded-xl px-3 py-2 mb-6">
              <h3 className="font-bold text-lg">New Applications</h3>
            </div>

            {/* SEARCH */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by name, title etc"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* TABLE */}
            <div className="border rounded-lg overflow-visible">
              <table className="w-full">
                <thead className="bg-[#f5f8ff] text-sm uppercase">
                  <tr>
                    <th className="py-3 text-left w-[5%]">#</th>

                    <th className="py-3 text-left w-[20%]">Candidate Info</th>

                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Submitted By
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("submitByType")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[20%]">
                      <div className="flex items-center gap-2">
                        Role Info
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("jobTitel")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Function
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("functionName")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[15%]">
                      <div className="flex items-center gap-2">
                        Department
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("department")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>
                    <th className="py-3 text-left w-[20%]">
                      <div className="flex items-center gap-2">
                        Grade
                        <Funnel
                          size={14}
                          onClick={() => setActiveFilter("grade")}
                          className="cursor-pointer text-primary"
                        />
                      </div>
                    </th>

                    <th className="py-3 text-center w-[10%]">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
                      {[...Array(5)].map((_, rowIdx) => (
                        <tr key={rowIdx} className="border-t">
                          {[...Array(7)].map((__, colIdx) => (
                            <td key={colIdx} className="py-4 px-3">
                              <Skeleton height={20} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </SkeletonTheme>
                  ) : currentCandidates.length ? (
                    currentCandidates.map((candidate, idx) => (
                      <tr key={idx} className="border-t hover:bg-gray-50">
                        <td className="py-6">{indexOfFirst + idx + 1}</td>
                        {/* <td>{candidate.roleCode || "--"}</td> */}
                        <td className="cursor-pointer text-left">
                          <div className="flex flex-col items-start">
                            <span
                              className="text-blue-500 font-semibold"
                              onClick={() => handleNameClick(candidate)}
                            >
                              {candidate.name || "-"}
                            </span>

                            <span className="text-gray-600 text-sm">
                              {candidate.mobile || "-"}
                            </span>

                            <span className="text-gray-500 text-xs">
                              {candidate.email || "-"}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.submitedBy}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.submitByType}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {formatDate(candidate.submitDate)}
                            </span>
                          </div>
                        </td>

                        <td className="text-left">
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-semibold">
                              {candidate.roleCode || "-"}
                            </span>

                            <span className="text-gray-500 text-sm">
                              {candidate.jobTitel || "-"}
                            </span>
                          </div>
                        </td>
                        <td>{candidate.functionName}</td>
                        <td>{candidate.department}</td>
                        <td>{candidate.grade}</td>

                        {/* <td
                          className="text-blue-500 font-semibold cursor-pointer"
                          onClick={() => handleNameClick(candidate)}
                        >
                          {candidate.name}
                        </td> */}

                        <td className="text-center">
                          <div className="flex items-center justify-center gap-3 relative">
                            {/* Resume Icon */}
                            <button
                              onClick={() => handleResumeClick(candidate)}
                              className="p-1 hover:bg-gray-100 rounded-full"
                              title="View Resume"
                            >
                              <FileText size={18} className="text-blue-500" />
                            </button>

                            {/* Three Dot Menu */}
                            <div className="relative inline-block action-menu">
                              <button
                                onClick={() =>
                                  setOpenMenu(
                                    openMenu === candidate.submitedCandidateUID
                                      ? null
                                      : candidate.submitedCandidateUID,
                                  )
                                }
                                className="p-1 hover:bg-gray-100 rounded-full"
                              >
                                <MoreVertical size={18} />
                              </button>

                              {openMenu === candidate.submitedCandidateUID && (
                                <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-50">
                                  <button
                                    onClick={async () => {
                                      try {
                                        setOpenMenu(null);

                                        const res = await GetCandidateDetails(
                                          candidate.submitedCandidateUID,
                                        );

                                        if (res?.data?.status === "Success") {
                                          setCandidateDetails(res.data);
                                          setSelectedCandidate(candidate);
                                          setModalActionType("shortlist");
                                          setShowModal(true);
                                        }
                                      } catch (err) {
                                        console.error(err);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    ShortList
                                  </button>

                                  <button
                                    onClick={async () => {
                                      try {
                                        setOpenMenu(null);

                                        const res = await GetCandidateDetails(
                                          candidate.submitedCandidateUID,
                                        );

                                        if (res?.data?.status === "Success") {
                                          setCandidateDetails(res.data);
                                          setSelectedCandidate(candidate);
                                          setModalActionType("reject");
                                          setShowModal(true);
                                        }
                                      } catch (err) {
                                        console.error(err);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                                  >
                                    Reject
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="py-6 text-red-500 text-center">
                        No Candidate Found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            <Pagination
              totalItems={filteredCandidates.length}
              itemsPerPage={candidatesPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setCandidatesPerPage}
            />
          </div>
        </div>
      </div>

      {/* FILTER MODAL */}
      <ColumnFilterModal
        show={!!activeFilter}
        onHide={() => setActiveFilter(null)}
        title={activeFilter}
        items={filterOptions[activeFilter] || []}
        filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
          item?.toLowerCase().includes(filterSearch.toLowerCase()),
        )}
        selectedItems={filters[activeFilter] || []}
        setSelectedItems={(values) =>
          setFilters((prev) => ({
            ...prev,
            [activeFilter]: values,
          }))
        }
        searchText={filterSearch}
        setSearchText={setFilterSearch}
      />

      <ViewResumeModal
        show={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        resumeUrl={resumeUrl}
      />

      <CandidateDetailsModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setShowSystemRemark(false);
          setModalActionType(null);
        }}
        candidateDetails={candidateDetails}
        selectedCandidate={selectedCandidate}
        onViewSystemRemark={handleViewSystemRemark}
        systemRemarkData={systemRemarkData}
        showSystemRemark={showSystemRemark}
        modalActionType={modalActionType}
        remark={remark}
        setRemark={setRemark}
        onActionSubmit={handleFinalAction}
        actionLoading={actionLoading}
        showSystemRemarkButton={true}
        showResumeButton={false}
        showInterviewSection={false}
        systemRemarkLoading={systemRemarkLoading}
      />

      <SystemRemarkModal
        show={showSystemRemarkModal}
        onClose={() => setShowSystemRemarkModal(false)}
        systemRemarkData={systemRemarkData}
      />

      {/* ALERT */}
      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default NewApplications;
